import { useCallback, useEffect, useRef, useState } from "react";

const EVENT = "recify-storage";

export const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error("Failed to write", key, e);
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { key } }));
};

/**
 * useState backed by localStorage. Every component using the same key stays
 * in sync (same tab through a custom event, other tabs through `storage`).
 * The setter accepts a value or an updater function, like useState.
 */
export function useStoredState(key, initial) {
  const initialRef = useRef(initial);
  const [value, setValue] = useState(() => readJSON(key, initialRef.current));

  useEffect(() => {
    const sync = (e) => {
      const changed = e.type === "storage" ? e.key === key : e.detail?.key === key;
      if (changed) setValue(readJSON(key, initialRef.current));
    };
    window.addEventListener("storage", sync);
    window.addEventListener(EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(EVENT, sync);
    };
  }, [key]);

  const set = useCallback(
    (next) => {
      const current = readJSON(key, initialRef.current);
      writeJSON(key, typeof next === "function" ? next(current) : next);
    },
    [key]
  );

  return [value, set];
}

export const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
